/**
 * 同じファイルから作る素材の要素を**1 本に共有する**ための道具(#168)。
 *
 * 正本は [ADR-0031](../../../docs/adr/0031-source-keyed-upload.md) の決定 4。
 *
 * ## なぜ共有するか
 *
 * 以前は**ドロップごとに `<video>` を作っていた。** 同じ動画を 4 セルに
 * 入れれば**復号器が 4 本**立ち上がる —— 中身は同じ画素なのに。
 *
 * ブラウザが同時に使えるハードウェア復号器には上限があり、超えると
 * ソフトウェア復号に落ちて **CPU が張り付く。** 利用者の報告
 * 「一部の動画だけ進みが遅い」はこの形で起こる。
 *
 * ## なぜ参照カウンタを持たないか
 *
 * **割り当ての表から導出する。** カウンタは数え間違いが即座に
 * 「絵が消える」「要素が残る」に化けるうえ、**表と食い違っても気づけない。**
 *
 * 「この素材を使っている割り当てが他にあるか」は表を見れば必ず正しく分かる ——
 * [`isStillUsed`](#isStillUsed) がそれを見る。
 *
 * ## なぜ描画コアに置くか
 *
 * **ビューアと S04 の両方が使う。** 同じ規則が 2 か所に生まれるのを防ぐ
 * (`source.ts` と同じ理由)。`packages/shared` には置けない ——
 * あちらは API(Node)からも読まれ、**DOM の型を持たない。**
 */
/**
 * ファイルの同一性の鍵(#168)。
 *
 * **名前・サイズ・更新時刻・MIME の組で見る。** 内容のハッシュは取らない ——
 * 4K 動画で数百 MB を読む待ちに見合う価値がない
 * ([ADR-0031](../../../docs/adr/0031-source-keyed-upload.md) の代替案 B)。
 *
 * **4 つ全部が一致する別のファイルを同時に扱う状況は実用上考えにくい。**
 * 万一当たっても、起こるのは「同じに見える別の動画が共有される」ことで、
 * 壊れ方としては軽い。
 *
 * ## なぜ `JSON.stringify` か
 *
 * **区切り文字を選ぶ問題をなくすため。** 名前と MIME は任意の文字列なので、
 * `/` や `:` や空白で連結すると**名前の中の区切りと混ざって別のファイルが
 * 同じ鍵になる**(`"a 1" + 2` と `"a" + "1 2"`)。
 *
 * JSON の文字列化は引用符と escape を付けるので**単射**である。
 * NUL を区切りに使う手もあるが、**生の NUL をソースに書くと git が
 * ファイルをバイナリと判定して差分が読めなくなる**(実際に踏んだ)。
 */
export function fileKeyOf(file) {
    // `lastModified` を持たない実装がある(古い Safari)。**無ければ 0 に倒す** ——
    // 共有されやすくなるだけで、壊れる方向ではない。
    const modified = typeof file.lastModified === 'number' ? file.lastModified : 0;
    return JSON.stringify([file.name, file.size, modified, file.type]);
}
/**
 * 同じファイルから作った素材が既にあれば返す(#168)。
 *
 * **無ければ `null`** —— 呼び出し側が新しく作る。
 *
 * **`await` をまたいで結果を持ち回らない。** 待っている間に割り当てが
 * 変わると、**破棄済みの要素を掴む。** 呼び出し側は待ったあとに
 * [`isStillUsed`](#isStillUsed) で生きているか確かめる。
 */
export function findShared(entries, fileKey) {
    for (const entry of entries) {
        if (entry.fileKey === fileKey)
            return entry.source;
    }
    return null;
}
/**
 * この素材を使っている割り当てがまだあるか(#168)。
 *
 * **表から外したあとに呼ぶ。** `false` なら破棄してよい。
 *
 * ```ts
 * assignments.delete(key);                             // 先に外す
 * if (!isStillUsed(assignments.values(), old)) destroy(old);
 * ```
 *
 * **順序を逆にすると永久に破棄されない**(自分自身を数えてしまう)。
 */
export function isStillUsed(entries, source) {
    for (const entry of entries) {
        if (entry.source === source)
            return true;
    }
    return false;
}
/**
 * 用済みになった素材のうち、**もう誰も使っていないものだけ**を破棄する(#168)。
 *
 * 共有を入れた結果、**破棄の判定が呼び出し側の 6 か所に増えた** ——
 * どれか 1 つで間違えれば「絵が消える」か「復号器が残る」。判定をここに寄せる。
 *
 * ## 2 つの落とし穴を両方ふさぐ
 *
 * | 落とし穴 | ふさぎ方 |
 * |---|---|
 * | 共有している素材を捨てる → **他のセルの絵が消える** | `remaining` に居れば捨てない |
 * | `stale` に同じ素材が何度も入る(まとめて解除)→ **2 度捨てる** | 集合にまとめてから捨てる |
 *
 * **破棄そのものは差し込みにする。** この関数を DOM から切り離しておけば
 * 検査できる(WebGL / DOM は jsdom で試さない —— ADR-0019)。
 *
 * `remaining` は**表から `stale` を外したあとの状態**を渡す。
 */
export function destroyUnused(stale, remaining, destroy) {
    const gone = new Set();
    // **`remaining` を毎回なめ直さない。** 先に集合にしておく。
    const kept = new Set();
    for (const entry of remaining)
        kept.add(entry.source);
    for (const entry of stale) {
        if (!kept.has(entry.source))
            gone.add(entry.source);
    }
    for (const source of gone)
        destroy(source);
}
/**
 * 表に載っている**別々の素材**を返す(#168)。
 *
 * 共有していれば割り当ての数より少なくなる ——
 * **これが同時に走る復号器の本数**であり、**再生・停止・同期を掛ける相手**でもある。
 *
 * **重複したまま `play()` や `currentTime = 0` を掛けない。** 同じ要素に
 * 何度も掛けると、掛けた回数だけ余計な仕事が増える(#159 の同期)。
 */
export function distinctSources(entries) {
    return [...new Set([...entries].map((entry) => entry.source))];
}
//# sourceMappingURL=source-pool.js.map